'use client';

import { Box, Typography, Divider, Paper } from '@mui/material';
import RequirementToggle from '../components/RequirementToggle';
import { categoryConfig, CategoryNode } from '../CategoryConfig';

interface RequirementsStepProps {
  discipline: string;
  selectedCategories: string[];
  requirements: {
    [key: string]: {
      must: boolean;
      preferred: boolean;
    };
  };
  onRequirementChange: (categoryId: string, value: { must: boolean; preferred: boolean }) => void;
}

// Helper function to find a category node by ID
const findCategoryNode = (
  id: string,
  nodes: CategoryNode[],
  path: CategoryNode[] = []
): CategoryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findCategoryNode(id, node.children, [...path, node]);
      if (found) return found;
    }
  }
  return null;
};

export default function RequirementsStep({
  discipline,
  selectedCategories,
  requirements,
  onRequirementChange,
}: RequirementsStepProps) {
  const categories = categoryConfig[discipline as keyof typeof categoryConfig];

  return (
    <div>
      <Typography variant="h5" gutterBottom>
        Set Requirements
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        For each selected category, specify which aspects are must-have requirements and which are
        preferred but flexible.
      </Typography>

      {selectedCategories.map((categoryId, index) => {
        const category = findCategoryNode(categoryId, categories);
        if (!category) return null;

        return (
          <Box key={categoryId} sx={{ mb: 4 }}>
            {index > 0 && <Divider sx={{ my: 4 }} />}
            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography variant="h6" gutterBottom>
                {category.label}
              </Typography>
              {category.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {category.description}
                </Typography>
              )}

              {/* Core Requirements */}
              <RequirementToggle
                label="Core Requirements"
                description="Basic requirements for this category"
                value={requirements[categoryId] || { must: false, preferred: false }}
                onChange={value => onRequirementChange(categoryId, value)}
              />

              {/* Additional requirements based on category type */}
              {categoryId.includes('3d-printing') && (
                <>
                  <RequirementToggle
                    label="Layer Height"
                    description="Specify if you need a particular layer height"
                    value={
                      requirements[`${categoryId}-layer-height`] || {
                        must: false,
                        preferred: false,
                      }
                    }
                    onChange={value => onRequirementChange(`${categoryId}-layer-height`, value)}
                  />
                  <RequirementToggle
                    label="Surface Finish"
                    description="Requirements for the final surface quality"
                    value={
                      requirements[`${categoryId}-surface`] || {
                        must: false,
                        preferred: false,
                      }
                    }
                    onChange={value => onRequirementChange(`${categoryId}-surface`, value)}
                  />
                </>
              )}

              {categoryId.includes('cnc') && (
                <>
                  <RequirementToggle
                    label="Tolerance"
                    description="Specify if you need particular dimensional tolerances"
                    value={
                      requirements[`${categoryId}-tolerance`] || {
                        must: false,
                        preferred: false,
                      }
                    }
                    onChange={value => onRequirementChange(`${categoryId}-tolerance`, value)}
                  />
                  <RequirementToggle
                    label="Surface Finish"
                    description="Requirements for the final surface quality"
                    value={
                      requirements[`${categoryId}-surface`] || {
                        must: false,
                        preferred: false,
                      }
                    }
                    onChange={value => onRequirementChange(`${categoryId}-surface`, value)}
                  />
                </>
              )}

              {categoryId.includes('laser-cutting') && (
                <>
                  <RequirementToggle
                    label="Edge Quality"
                    description="Requirements for the cut edge quality"
                    value={
                      requirements[`${categoryId}-edge`] || {
                        must: false,
                        preferred: false,
                      }
                    }
                    onChange={value => onRequirementChange(`${categoryId}-edge`, value)}
                  />
                  <RequirementToggle
                    label="Material Thickness"
                    description="Specify if you need a particular material thickness"
                    value={
                      requirements[`${categoryId}-thickness`] || {
                        must: false,
                        preferred: false,
                      }
                    }
                    onChange={value => onRequirementChange(`${categoryId}-thickness`, value)}
                  />
                </>
              )}
            </Paper>
          </Box>
        );
      })}
    </div>
  );
}
